// Utilitário para acesso seguro ao localStorage
// Evita erros em contextos onde o localStorage não está disponível ou é restrito
// Usa uma implementação em memória como fallback

// Armazenamento em memória como fallback
const memoryStorage: Record<string, string> = {};

// Verificar se o localStorage está disponível
const isLocalStorageAvailable = (): boolean => {
  try {
    const testKey = '__storage_test__';
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
};

// Flag para indicar se o localStorage está disponível
const storageAvailable = isLocalStorageAvailable();

// Se o localStorage não estiver disponível, mostrar um aviso uma vez
if (!storageAvailable) {
  console.warn('localStorage não está disponível. Usando armazenamento em memória como fallback.');
}

export const safeStorage = {
  getItem: (key: string): string | null => {
    if (storageAvailable) {
      try {
        return localStorage.getItem(key);
      } catch (e) {
        console.warn('Erro ao acessar localStorage:', e);
        // Fallback para armazenamento em memória
        return memoryStorage[key] || null;
      }
    } else {
      // Usar armazenamento em memória
      return memoryStorage[key] || null;
    }
  },

  setItem: (key: string, value: string): void => {
    if (storageAvailable) {
      try {
        localStorage.setItem(key, value);
      } catch (e) {
        console.warn('Erro ao escrever no localStorage:', e);
        // Fallback para armazenamento em memória
        memoryStorage[key] = value;
      }
    } else {
      // Usar armazenamento em memória
      memoryStorage[key] = value;
    }
  },

  removeItem: (key: string): void => {
    if (storageAvailable) {
      try {
        localStorage.removeItem(key);
      } catch (e) {
        console.warn('Erro ao remover item do localStorage:', e);
      }
    }
    // Sempre remover do armazenamento em memória
    delete memoryStorage[key];
  },

  clear: (): void => {
    if (storageAvailable) {
      try {
        localStorage.clear();
      } catch (e) {
        console.warn('Erro ao limpar localStorage:', e);
      }
    }
    // Sempre limpar o armazenamento em memória
    Object.keys(memoryStorage).forEach(key => {
      delete memoryStorage[key];
    });
  }
};

// Função para interceptar erros de acesso ao storage
export const setupStorageErrorHandler = (): void => {
  window.addEventListener('error', (event) => {
    if (
      event.message.includes('storage') ||
      event.message.includes('localStorage') ||
      event.message.includes('Access to storage')
    ) {
      console.warn('Erro de acesso ao storage interceptado:', event.message);
      event.preventDefault();
      event.stopPropagation();
      return true;
    }
    return false;
  });

  // Também intercepta erros em promessas não tratadas
  window.addEventListener('unhandledrejection', (event) => {
    if (
      event.reason &&
      typeof event.reason.message === 'string' &&
      (
        event.reason.message.includes('storage') ||
        event.reason.message.includes('localStorage') ||
        event.reason.message.includes('Access to storage')
      )
    ) {
      console.warn('Erro de acesso ao storage em promessa interceptado:', event.reason.message);
      event.preventDefault();
      return true;
    }
    return false;
  });
};
